import React, { useState, useEffect } from 'react';
import { Compass, ArrowLeft, Sparkles, Plus } from 'lucide-react';
import { Button } from '../components/Button';
import { cn } from '../utils/cn';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { CoreValue } from '../types';
import { ProtectedLayout } from '../components/ProtectedLayout';

const coreValuesList: CoreValue[] = [
  'Achievement', 'Authenticity', 'Clarity', 'Connection', 'Contribution', 'Courage',
  'Discipline', 'Excellence', 'Faith', 'Family', 'Friendship',
  'Growth', 'Hustle', 'Impact', 'Integrity', 'Kindness', 'Leadership', 'Loyalty',
  'Mastery', 'Passion', 'Presence', 'Purpose', 'Resilience', 'Responsibility',
  'Service', 'Structures', 'Success', 'Systems', 'Teaching', 'Vision'
];

const sparkWordsList: CoreValue[] = [
  'Adventure', 'Boldness', 'Creativity', 'Curiosity',
  'Dancing', 'Discovery', 'Energy', 'Exercise', 'Exploration',
  'Flow', 'Fun', 'Imagination', 'Laughter', 'Momentum', 'Movement', 'Music', 'Nature',
  'Play', 'Pleasure', 'Spontaneity', 'Vitality', 'Wonder'
];

type SelectedValue = { value: string; group: 'cv' | 'sw' };

export function CoreValues() {
  const navigate = useNavigate();
  const [selectedValues, setSelectedValues] = useState<SelectedValue[]>([]);
  const [customCV, setCustomCV] = useState('');
  const [customSW, setCustomSW] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [coreWord, setCoreWord] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('core_word, name, email')
          .eq('id', user.id)
          .single();

        if (profile) {
          if (profile.core_word) setCoreWord(profile.core_word);
          if (profile.name) setFullName(profile.name);
          if (profile.email) setEmail(profile.email);
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchProfileData();
  }, []);

  const hasSparkWord = selectedValues.some(sv => sv.group === 'sw');
  const isValid = selectedValues.length === 3 && hasSparkWord;

  const handleSelect = (value: string, group: 'cv' | 'sw') => {
    const exists = selectedValues.find(sv => sv.value === value);
    if (exists) {
      setSelectedValues(prev => prev.filter(sv => sv.value !== value));
    } else if (selectedValues.length < 3) {
      setSelectedValues(prev => [...prev, { value, group }]);
    }
  };

  const handleAddCustom = (group: 'cv' | 'sw') => {
    const raw = group === 'cv' ? customCV : customSW;
    const trimmed = raw.trim();
    if (!trimmed || selectedValues.length >= 3) return;
    if (selectedValues.find(sv => sv.value.toLowerCase() === trimmed.toLowerCase())) return;
    setSelectedValues(prev => [...prev, { value: trimmed, group }]);
    if (group === 'cv') setCustomCV('');
    else setCustomSW('');
  };

  const sendWebhook = async (values: string[]) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) return;

      const [first_name = '', last_name = ''] = fullName.split(' ');
      const payload = {
        eventType: 'core_profile_update',
        payload: {
          full_name: fullName, first_name, last_name, email,
          core_word: coreWord,
          core_value_1: values[0],
          core_value_2: values[1],
          core_value_3: values[2]
        }
      };

      const res = await fetch('https://cdnbjnapvlbmarhuflsv.supabase.co/functions/v1/trigger-reminder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(payload)
      });
      if (!res.ok) console.error('Webhook failed:', await res.text());
    } catch (err) {
      console.error('Webhook error:', err);
    }
  };

  const handleNext = async () => {
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const values = selectedValues.map(sv => sv.value);
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ core_value_1: values[0], core_value_2: values[1], core_value_3: values[2] })
        .eq('id', user.id);

      if (updateError) throw updateError;
      await sendWebhook(values);
      navigate('/core-value-confirmation');
    } catch (error: any) {
      console.error('Error updating core values:', error);
      setError(error.message || 'Failed to update core values');
    } finally {
      setIsSubmitting(false);
    }
  };

  const ValueChip = ({ value, group }: { value: string; group: 'cv' | 'sw' }) => {
    const isSelected = !!selectedValues.find(sv => sv.value === value);
    const isDisabled = selectedValues.length === 3 && !isSelected;

    return (
      <button
        onClick={() => handleSelect(value, group)}
        disabled={isDisabled}
        className={cn(
          'px-4 py-2 rounded-full text-sm font-medium border-2 transition-all duration-150 select-none',
          group === 'cv'
            ? isSelected
              ? 'bg-[#004060] border-[#004060] text-white shadow-sm'
              : 'border-[#004060]/30 text-[#004060] hover:border-[#004060] hover:bg-[#004060]/5'
            : isSelected
              ? 'bg-amber-500 border-amber-500 text-white shadow-sm'
              : 'border-amber-400/50 text-amber-700 hover:border-amber-400 hover:bg-amber-50',
          isDisabled && 'opacity-40 cursor-not-allowed'
        )}
      >
        {value}
      </button>
    );
  };

  const CustomInput = ({ group }: { group: 'cv' | 'sw' }) => {
    const value = group === 'cv' ? customCV : customSW;
    const setter = group === 'cv' ? setCustomCV : setCustomSW;
    const disabled = selectedValues.length >= 3;

    return (
      <div className={cn(
        'flex items-center gap-2 px-3 py-1.5 rounded-full border-2 border-dashed transition-all',
        group === 'cv' ? 'border-[#004060]/30' : 'border-amber-400/50',
        disabled && 'opacity-40'
      )}>
        <input
          type="text"
          value={value}
          onChange={e => setter(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustom(group); } }}
          placeholder="Other..."
          disabled={disabled}
          className={cn(
            'bg-transparent text-sm font-medium outline-none w-24 placeholder-gray-400',
            group === 'cv' ? 'text-[#004060]' : 'text-amber-700'
          )}
        />
        <button
          onClick={() => handleAddCustom(group)}
          disabled={disabled || !value.trim()}
          className={cn(
            'rounded-full transition-colors',
            group === 'cv' ? 'text-[#004060]' : 'text-amber-600',
            (!value.trim() || disabled) && 'opacity-30 cursor-not-allowed'
          )}
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    );
  };

  const validMsg = selectedValues.length === 3 && !hasSparkWord
    ? 'Please select at least one Spark Word.'
    : null;

  return (
    <ProtectedLayout>
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-3xl w-full bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center gap-3 mb-4">
            <Compass className="w-8 h-8 text-[#236caf]" />
            <h1 className="text-3xl font-bold text-[#004060]">Core Values</h1>
          </div>

          <p className="text-gray-600 mb-2">
            Nice work. Now let's go deeper. Big words like{' '}
            <span className="font-semibold text-[#004060]">{coreWord || 'your core word'}</span>{' '}
            mean something different to everyone. Pick three words that bring{' '}
            <span className="font-semibold text-[#004060]">{coreWord || 'your core word'}</span>{' '}
            to life for you personally. What makes you feel it? What does it look like when you're living it?
          </p>
          <p className="text-sm text-gray-500 mb-8">
            Select 3 words and make sure at least one is a Spark Word.
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          {/* Core Values group */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-[#004060]" />
              <h2 className="text-base font-semibold text-[#004060] uppercase tracking-wide">Core Values</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {coreValuesList.map(v => <ValueChip key={v} value={v} group="cv" />)}
              <CustomInput group="cv" />
            </div>
          </div>

          {/* Spark Words group */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <h2 className="text-base font-semibold text-amber-600 uppercase tracking-wide">Spark Words</h2>
              <span className="text-xs text-amber-500 font-medium">(at least 1 required)</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {sparkWordsList.map(v => <ValueChip key={v} value={v} group="sw" />)}
              <CustomInput group="sw" />
            </div>
          </div>

          {/* Selection summary */}
          {selectedValues.length > 0 && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-700">
                  Selected ({selectedValues.length}/3)
                </h3>
                {validMsg && <span className="text-xs text-amber-600 font-medium">{validMsg}</span>}
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedValues.map(({ value, group }) => (
                  <div
                    key={value}
                    className={cn(
                      'px-3 py-1 rounded-full border flex items-center gap-1.5 text-sm font-medium',
                      group === 'sw'
                        ? 'bg-amber-50 border-amber-400 text-amber-700'
                        : 'bg-white border-[#236caf] text-[#236caf]'
                    )}
                  >
                    {group === 'sw' && <Sparkles className="w-3 h-3" />}
                    {value}
                    <button
                      onClick={() => setSelectedValues(prev => prev.filter(sv => sv.value !== value))}
                      className="opacity-60 hover:opacity-100 leading-none"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <Button onClick={() => navigate('/core-word')} variant="secondary" className="flex-1">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={!isValid || isSubmitting}
              className={cn('flex-1', (!isValid || isSubmitting) && 'opacity-50 cursor-not-allowed')}
            >
              {isSubmitting ? 'Saving...' : 'Continue'}
            </Button>
          </div>
        </div>
      </div>
    </ProtectedLayout>
  );
}
